import { useState, useContext, useEffect } from 'react';
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';

export default function LoginSuperAdmin() {
    const { 
        loginSuperAdmin,
        authError, 
        clearError, 
        isAuthenticated,
        user,
        isLoading,
        loadingMessage
    } = useContext(AuthContext);
    const navigate = useNavigate();
    const { showError, showSuccess } = useToast();

    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            showSuccess('Connexion réussie ! Redirection en cours...');
            setTimeout(() => {
                if (user.is_staff && user.is_superuser) {
                    navigate("/admin-panel");
                } else {
                    showError("Informations sur l'organisation absentes ou sans rôle");
                    navigate("/login");
                }
            }, 1000);
        }
    }, [isAuthenticated, user, navigate, showSuccess, showError]);

    useEffect(() => {
        if (authError) {
            showError(authError);
            if (clearError) clearError();
        }
    }, [authError, showError, clearError]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        
        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        }
        
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validate form
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Create a synthetic form event object that loginSuperAdmin expects
        const formEvent = {
            preventDefault: () => {},
            target: {
                email: { value: formData.email },
                password: { value: formData.password },
                rememberMe: { value: formData.rememberMe }
            }
        };

        // Call loginSuperAdmin with the proper event format
        loginSuperAdmin(formEvent);
    };

    return (
        <>
            {/* Loader overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4">
                        <div className="relative w-12 h-12">
                            <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
                        </div>
                        <p className="text-sm text-gray-700 font-medium">{loadingMessage}</p>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-white flex items-center justify-center p-4">
                <div className="w-full max-w-sm">
                    {/* Card */}
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                        
                        {/* Header */}
                        <div className="mb-8">
                            <h1 className="text-2xl font-bold text-gray-900 mb-1">Admin Access</h1>
                            <p className="text-sm text-gray-500">Super Administrator Login</p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-5">
                            
                            {/* Email Field */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Email</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="admin@example.com"
                                        className={`w-full pl-9 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                                            errors.email 
                                                ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                                                : 'border-gray-200 focus:ring-blue-500'
                                        }`}
                                        disabled={isLoading}
                                    />
                                </div>
                                {errors.email && (
                                    <div className="mt-1.5 flex items-center space-x-1">
                                        <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                                        <p className="text-xs text-red-600">{errors.email}</p>
                                    </div>
                                )}
                            </div>

                            {/* Password Field */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Password</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="••••••••"
                                        className={`w-full pl-9 pr-10 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                                            errors.password 
                                                ? 'border-red-300 focus:ring-red-500 bg-red-50' 
                                                : 'border-gray-200 focus:ring-blue-500'
                                        }`}
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                                        disabled={isLoading}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div className="mt-1.5 flex items-center space-x-1">
                                        <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                                        <p className="text-xs text-red-600">{errors.password}</p>
                                    </div>
                                )}
                            </div>

                            {/* Remember Me Checkbox */}
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="rememberMe"
                                    id="rememberMe"
                                    checked={formData.rememberMe}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 rounded border-gray-300"
                                    disabled={isLoading}
                                />
                                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-600">
                                    Remember me
                                </label>
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-green-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                            >
                                {isLoading ? 'Signing in...' : 'Sign In'}
                            </button>
                        </form>

                        {/* Footer */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <p className="text-xs text-gray-500 text-center">
                                © 2025 Admin Panel - Restricted Access
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}